import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Search, ArrowUpCircle, ArrowDownCircle, AlertTriangle, Download, ScanLine, Package, Trash2 } from 'lucide-react';
import { parseApiError } from '../lib/parseApiError';
import { uid } from '../lib/uid';
import BarcodeScannerModal from '../components/BarcodeScannerModal';
import BulkCaricoDialog from '../components/BulkCaricoDialog';
import ArticoliGroupedTable from '../components/ArticoliGroupedTable';
import CasseReseTab from '../components/CasseReseTab';
import VendutoTab from '../components/VendutoTab';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIE = ['Registratori', 'Stampanti', 'Accessori', 'Consumabili', 'Ricambi', 'Altro'];

export default function MagazzinoPage() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.ruolo === 'admin' || user?.role === 'admin' || user?.ruolo === 'super_admin' || user?.role === 'super_admin') && !user?.tenant_id;
  const [articoli, setArticoli] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [movimentoDialogOpen, setMovimentoDialogOpen] = useState(false);
  const [caricoUnits, setCaricoUnits] = useState(false);
  const [unitRows, setUnitRows] = useState([{ matricola: '', codice_barre: '', _uid: uid() }]);
  const [editingArticolo, setEditingArticolo] = useState(null);
  const [selectedArticolo, setSelectedArticolo] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [bulkCaricoOpen, setBulkCaricoOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    codice: '',
    codice_barre: '',
    descrizione: '',
    unita_misura: 'pz',
    prezzo_acquisto: '',
    prezzo_vendita: '',
    quantita: '',
    scorta_minima: '',
    categoria: '',
    note: '',
    marca: '',
    modello: '',
    matricola: ''
  });

  const [movimentoForm, setMovimentoForm] = useState({
    articolo_id: '',
    tipo: 'carico',
    quantita: '',
    prezzo_unitario: '',
    riferimento: '',
    note: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [articoliRes, movimentiRes] = await Promise.all([
        axios.get(`${API}/magazzino/articoli`),
        axios.get(`${API}/magazzino/movimenti`)
      ]);
      setArticoli(articoliRes.data);
      setMovimenti(movimentiRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        prezzo_acquisto: parseFloat(formData.prezzo_acquisto) || 0,
        prezzo_vendita: parseFloat(formData.prezzo_vendita) || 0,
        quantita: parseFloat(formData.quantita) || 0,
        scorta_minima: parseFloat(formData.scorta_minima) || 0
      };
      
      if (editingArticolo) {
        await axios.put(`${API}/magazzino/articoli/${editingArticolo.id}`, data);
        toast.success('Articolo aggiornato');
      } else {
        await axios.post(`${API}/magazzino/articoli`, data);
        toast.success('Articolo creato');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    }
  };

  const validUnitRows = unitRows.filter((u) => u.matricola.trim() || u.codice_barre.trim());

  const handleMovimento = async (e) => {
    e.preventDefault();
    // Carico per unità: ogni pezzo con la sua matricola/EAN (riusa il bulk-carico)
    if (movimentoForm.tipo === 'carico' && caricoUnits) {
      if (validUnitRows.length === 0) {
        toast.error('Inserisci almeno una matricola o codice a barre');
        return;
      }
      try {
        const payload = {
          descrizione: selectedArticolo.descrizione,
          marca: selectedArticolo.marca || null,
          modello: selectedArticolo.modello || null,
          categoria: selectedArticolo.categoria || null,
          unita_misura: selectedArticolo.unita_misura || 'pz',
          prezzo_acquisto: movimentoForm.prezzo_unitario ? parseFloat(movimentoForm.prezzo_unitario) : (selectedArticolo.prezzo_acquisto || 0),
          prezzo_vendita: selectedArticolo.prezzo_vendita || 0,
          codice_prefix: selectedArticolo.codice || null,
          riferimento: movimentoForm.riferimento || null,
          note: movimentoForm.note || null,
          units: validUnitRows.map((u) => ({
            codice_barre: u.codice_barre.trim() || null,
            matricola: u.matricola.trim() || null,
          })),
        };
        const { data } = await axios.post(`${API}/magazzino/articoli/bulk-carico`, payload);
        if (data.created_count > 0) {
          toast.success(`✅ Caricate ${data.created_count} unità di ${selectedArticolo.descrizione}`);
        }
        if (data.skipped_count > 0) {
          const dettagli = (data.skipped || []).map((s) => `${s.matricola || s.codice_barre || s.codice}: ${s.reason}`).join(' • ');
          toast.warning(`${data.skipped_count} unità saltate — ${dettagli}`, { duration: 8000 });
        }
        setMovimentoDialogOpen(false);
        resetMovimentoForm();
        fetchData();
      } catch (error) {
        toast.error(parseApiError(error, 'Errore nel carico unità'));
      }
      return;
    }
    try {
      const data = {
        ...movimentoForm,
        quantita: parseFloat(movimentoForm.quantita) || 0,
        prezzo_unitario: movimentoForm.prezzo_unitario ? parseFloat(movimentoForm.prezzo_unitario) : null
      };
      
      await axios.post(`${API}/magazzino/movimenti`, data);
      toast.success(`Movimento ${data.tipo} registrato`);
      setMovimentoDialogOpen(false);
      resetMovimentoForm();
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    }
  };

  const handleEdit = (articolo) => {
    setEditingArticolo(articolo);
    setFormData({
      codice: articolo.codice,
      codice_barre: articolo.codice_barre || '',
      descrizione: articolo.descrizione,
      unita_misura: articolo.unita_misura || 'pz',
      prezzo_acquisto: articolo.prezzo_acquisto?.toString() || '',
      prezzo_vendita: articolo.prezzo_vendita?.toString() || '',
      quantita: articolo.quantita?.toString() || '',
      scorta_minima: articolo.scorta_minima?.toString() || '',
      categoria: articolo.categoria || '',
      note: articolo.note || '',
      marca: articolo.marca || '',
      modello: articolo.modello || '',
      matricola: articolo.matricola || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo articolo e tutti i suoi movimenti?')) return;
    try {
      await axios.delete(`${API}/magazzino/articoli/${id}`);
      toast.success('Articolo eliminato');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore eliminazione'));
    }
  };

  const openMovimentoDialog = (articolo, tipo) => {
    setSelectedArticolo(articolo);
    setCaricoUnits(false);
    setUnitRows([{ matricola: '', codice_barre: '', _uid: uid() }]);
    setMovimentoForm({
      articolo_id: articolo.id,
      tipo: tipo,
      quantita: '',
      prezzo_unitario: tipo === 'carico' ? articolo.prezzo_acquisto?.toString() || '' : articolo.prezzo_vendita?.toString() || '',
      riferimento: '',
      note: ''
    });
    setMovimentoDialogOpen(true);
  };

  const resetForm = () => {
    setEditingArticolo(null);
    setFormData({
      codice: '',
      codice_barre: '',
      descrizione: '',
      unita_misura: 'pz',
      prezzo_acquisto: '',
      prezzo_vendita: '',
      quantita: '',
      scorta_minima: '',
      categoria: '',
      note: '',
      marca: '',
      modello: '',
      matricola: ''
    });
  };

  // Callback quando lo scanner ha letto un codice
  const handleScanFromHeader = async (codice) => {
    try {
      const { data } = await axios.get(`${API}/magazzino/scan/${encodeURIComponent(codice)}`);
      // Se trovato → apri modifica
      toast.success(`Trovato: ${data.descrizione}`);
      handleEdit(data);
    } catch (error) {
      // Se non trovato → apri nuovo articolo pre-compilato con il codice
      const notFound = error?.response?.status === 404;
      if (notFound) {
        toast.info(`Codice non presente: aggiungo nuovo articolo con "${codice}"`);
        resetForm();
        setFormData(prev => ({ ...prev, codice_barre: codice, codice: codice }));
        setDialogOpen(true);
      } else {
        toast.error(parseApiError(error, 'Errore ricerca articolo'));
      }
    }
  };

  // I lettori barcode USB inviano Enter dopo il codice: blocca il submit implicito del form
  const preventEnterSubmit = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault();
  };

  const resetMovimentoForm = () => {
    setSelectedArticolo(null);
    setCaricoUnits(false);
    setUnitRows([{ matricola: '', codice_barre: '', _uid: uid() }]);
    setMovimentoForm({
      articolo_id: '',
      tipo: 'carico',
      quantita: '',
      prezzo_unitario: '',
      riferimento: '',
      note: ''
    });
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/export/magazzino`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'magazzino_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore export');
    }
  };

  const getArticoloName = (articoloId) => {
    const articolo = articoli.find(a => a.id === articoloId);
    return articolo ? `${articolo.codice} - ${articolo.descrizione}` : '-';
  };

  const articoliSottoScorta = articoli.filter(a => a.stato === 'disponibile' && a.quantita <= a.scorta_minima);

  // Tab Articoli = solo giacenza reale (i venduti vanno in "Venduto", rese/dismessi in "Rese")
  const articoliInGiacenza = articoli.filter(a => a.stato !== 'venduto' && a.stato !== 'reso' && a.stato !== 'dismesso');
  const countVenduti = articoli.filter(a => a.stato === 'venduto').length;
  const countRese = articoli.filter(a => a.stato === 'reso' || a.stato === 'dismesso').length;

  const filteredArticoli = articoliInGiacenza.filter(a => 
    a.codice?.toLowerCase().includes(search.toLowerCase()) ||
    a.descrizione?.toLowerCase().includes(search.toLowerCase()) ||
    a.categoria?.toLowerCase().includes(search.toLowerCase()) ||
    a.matricola?.toLowerCase().includes(search.toLowerCase()) ||
    a.codice_barre?.toLowerCase().includes(search.toLowerCase()) ||
    a.marca?.toLowerCase().includes(search.toLowerCase()) ||
    a.modello?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="magazzino-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Magazzino</h1>
          <p className="text-muted-foreground text-sm mt-1">{articoliInGiacenza.length} articoli in giacenza · {countVenduti} venduti</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setBulkCaricoOpen(true)} 
            className="rounded-none border-cyan-500/40 hover:bg-cyan-50 dark:hover:bg-cyan-950/20" 
            data-testid="bulk-carico-btn"
          >
            <Package className="h-4 w-4 mr-2 text-cyan-600" />
            Carico Multiplo
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setScannerOpen(true)} 
            className="rounded-none" 
            data-testid="scan-articolo-btn"
          >
            <ScanLine className="h-4 w-4 mr-2" />
            Scansiona
          </Button>
          <Button variant="outline" onClick={handleExport} className="rounded-none" data-testid="export-magazzino-btn">
            <Download className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-none font-mono uppercase tracking-wider" data-testid="new-articolo-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Articolo
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl rounded-none">
              <DialogHeader>
                <DialogTitle className="font-mono uppercase tracking-wider">
                  {editingArticolo ? 'Modifica Articolo' : 'Nuovo Articolo'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-mono uppercase">Codice *</Label>
                    <Input
                      value={formData.codice}
                      onChange={(e) => setFormData({...formData, codice: e.target.value})}
                      className="rounded-none font-mono"
                      required
                      disabled={!!editingArticolo}
                      data-testid="articolo-codice"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Codice a Barre (EAN/UPC)</Label>
                    <Input
                      value={formData.codice_barre}
                      onChange={(e) => setFormData({...formData, codice_barre: e.target.value})}
                      className="rounded-none font-mono"
                      placeholder="Scansiona o digita"
                      data-testid="articolo-codice-barre"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Categoria</Label>
                  <Select 
                    value={formData.categoria || "none"} 
                    onValueChange={(value) => setFormData({...formData, categoria: value === "none" ? "" : value})}
                  >
                    <SelectTrigger className="rounded-none" data-testid="articolo-categoria">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="none">Nessuna</SelectItem>
                      {CATEGORIE.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Descrizione *</Label>
                  <Input
                    value={formData.descrizione}
                    onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                    className="rounded-none"
                    required
                    data-testid="articolo-descrizione"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-mono uppercase">Unità Misura</Label>
                    <Input
                      value={formData.unita_misura}
                      onChange={(e) => setFormData({...formData, unita_misura: e.target.value})}
                      className="rounded-none"
                      data-testid="articolo-um"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Quantità Iniziale</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.quantita}
                      onChange={(e) => setFormData({...formData, quantita: e.target.value})}
                      className="rounded-none font-mono"
                      data-testid="articolo-quantita"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Scorta Minima</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.scorta_minima}
                      onChange={(e) => setFormData({...formData, scorta_minima: e.target.value})}
                      className="rounded-none font-mono"
                      data-testid="articolo-scorta"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-mono uppercase">Marca</Label>
                    <Input
                      value={formData.marca}
                      onChange={(e) => setFormData({...formData, marca: e.target.value})}
                      className="rounded-none"
                      placeholder="Es. Epson"
                      data-testid="articolo-marca"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Modello</Label>
                    <Input
                      value={formData.modello}
                      onChange={(e) => setFormData({...formData, modello: e.target.value})}
                      className="rounded-none"
                      placeholder="Es. TM-88"
                      data-testid="articolo-modello"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Matricola (opzionale)</Label>
                    <Input
                      value={formData.matricola}
                      onChange={(e) => setFormData({...formData, matricola: e.target.value})}
                      className="rounded-none font-mono"
                      placeholder="Se cassa singola"
                      data-testid="articolo-matricola"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {isSuperAdmin && (
                    <div>
                      <Label className="text-xs font-mono uppercase flex items-center gap-1">
                        Prezzo Acquisto (EUR)
                        <span className="text-[9px] bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 px-1.5 py-0.5 rounded font-bold">SOLO SUPER ADMIN</span>
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.prezzo_acquisto}
                        onChange={(e) => setFormData({...formData, prezzo_acquisto: e.target.value})}
                        className="rounded-none font-mono"
                        data-testid="articolo-prezzo-acquisto"
                      />
                    </div>
                  )}
                  <div className={isSuperAdmin ? '' : 'col-span-2'}>
                    <Label className="text-xs font-mono uppercase">Prezzo Vendita (EUR)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.prezzo_vendita}
                      onChange={(e) => setFormData({...formData, prezzo_vendita: e.target.value})}
                      className="rounded-none font-mono"
                      data-testid="articolo-prezzo-vendita"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Note</Label>
                  <Textarea
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    className="rounded-none"
                    data-testid="articolo-note"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                    Annulla
                  </Button>
                  <Button type="submit" className="rounded-none font-mono uppercase" data-testid="save-articolo-btn">
                    {editingArticolo ? 'Aggiorna' : 'Crea'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Alert sotto scorta */}
      {articoliSottoScorta.length > 0 && (
        <Card className="border-warning/50 bg-warning/5 rounded-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2 text-warning">
              <AlertTriangle className="h-4 w-4" />
              Articoli Sotto Scorta ({articoliSottoScorta.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {articoliSottoScorta.map(a => (
                <Badge key={a.id} variant="outline" className="rounded-none border-warning text-warning">
                  {a.codice}: {a.quantita} {a.unita_misura} (min: {a.scorta_minima})
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="articoli" className="w-full">
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent p-0">
          <TabsTrigger value="articoli" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6">
            Articoli
          </TabsTrigger>
          <TabsTrigger value="movimenti" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6">
            Movimenti
          </TabsTrigger>
          <TabsTrigger value="venduto" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6" data-testid="tab-venduto">
            Venduto{countVenduti > 0 ? ` (${countVenduti})` : ''}
          </TabsTrigger>
          <TabsTrigger value="rese" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6" data-testid="tab-rese">
            Rese{countRese > 0 ? ` (${countRese})` : ''}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="articoli" className="mt-4 space-y-4">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca articolo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-none"
              data-testid="search-articoli"
            />
          </div>

          {/* Table raggruppata: unità stesso modello in riga espandibile */}
          <ArticoliGroupedTable
            articoli={filteredArticoli}
            isSuperAdmin={isSuperAdmin}
            onCarico={(articolo) => openMovimentoDialog(articolo, 'carico')}
            onScarico={(articolo) => openMovimentoDialog(articolo, 'scarico')}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </TabsContent>

        <TabsContent value="movimenti" className="mt-4">
          <div className="border border-border overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Articolo</th>
                  <th>Quantità</th>
                  <th className="hidden md:table-cell">Prezzo</th>
                  <th className="hidden lg:table-cell">Riferimento</th>
                  <th className="hidden lg:table-cell">Utente</th>
                </tr>
              </thead>
              <tbody>
                {movimenti.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted-foreground py-8">
                      Nessun movimento registrato
                    </td>
                  </tr>
                ) : (
                  movimenti.map((mov) => (
                    <tr key={mov.id}>
                      <td className="font-mono text-xs">
                        {new Date(mov.data).toLocaleString('it-IT')}
                      </td>
                      <td>
                        <Badge className={`rounded-none ${mov.tipo === 'carico' ? 'bg-success/20 text-success border border-success/30' : 'bg-urgent/20 text-urgent border border-urgent/30'}`}>
                          {mov.tipo}
                        </Badge>
                      </td>
                      <td>{getArticoloName(mov.articolo_id)}</td>
                      <td className="font-mono">{mov.quantita}</td>
                      <td className="hidden md:table-cell font-mono">
                        {mov.prezzo_unitario ? `${mov.prezzo_unitario.toFixed(2)} €` : '-'}
                      </td>
                      <td className="hidden lg:table-cell">{mov.riferimento || '-'}</td>
                      <td className="hidden lg:table-cell text-xs">{mov.utente_nome}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="venduto" className="mt-4">
          <VendutoTab articoli={articoli} isSuperAdmin={isSuperAdmin} />
        </TabsContent>

        <TabsContent value="rese" className="mt-4">
          <CasseReseTab articoli={articoli} onRefresh={fetchData} />
        </TabsContent>
      </Tabs>

      {/* Movimento Dialog */}
      <Dialog open={movimentoDialogOpen} onOpenChange={(open) => { setMovimentoDialogOpen(open); if (!open) resetMovimentoForm(); }}>
        <DialogContent className="sm:max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono uppercase tracking-wider flex items-center gap-2">
              {movimentoForm.tipo === 'carico' ? (
                <><ArrowUpCircle className="h-5 w-5 text-success" /> Carico Magazzino</>
              ) : (
                <><ArrowDownCircle className="h-5 w-5 text-urgent" /> Scarico Magazzino</>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMovimento} onKeyDown={preventEnterSubmit} className="space-y-4">
            {selectedArticolo && (
              <div className="p-3 bg-muted border border-border">
                <p className="font-mono text-sm">{selectedArticolo.codice}</p>
                <p className="text-sm text-muted-foreground">{selectedArticolo.descrizione}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Disponibile: {selectedArticolo.quantita} {selectedArticolo.unita_misura}
                </p>
              </div>
            )}
            {movimentoForm.tipo === 'carico' && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCaricoUnits(false)}
                  className={`p-2.5 border text-left transition-colors ${!caricoUnits ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/20' : 'border-border hover:bg-muted/50'}`}
                  data-testid="carico-mode-quantita"
                >
                  <div className="flex items-center gap-2 font-semibold text-xs">
                    <Package className="h-4 w-4 text-cyan-600" /> Solo quantità
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">Somma pezzi senza codici</div>
                </button>
                <button
                  type="button"
                  onClick={() => setCaricoUnits(true)}
                  className={`p-2.5 border text-left transition-colors ${caricoUnits ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/20' : 'border-border hover:bg-muted/50'}`}
                  data-testid="carico-mode-units"
                >
                  <div className="flex items-center gap-2 font-semibold text-xs">
                    <ScanLine className="h-4 w-4 text-cyan-600" /> Unità con matricola
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">Matricola + EAN per ogni pezzo</div>
                </button>
              </div>
            )}
            {movimentoForm.tipo === 'carico' && caricoUnits ? (
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase">Unità da caricare ({validUnitRows.length})</Label>
                <div className="border max-h-[32vh] overflow-y-auto divide-y">
                  {unitRows.map((u, idx) => (
                    <div key={u._uid} className="grid grid-cols-12 gap-1.5 px-2 py-1.5 items-center">
                      <span className="col-span-1 text-[10px] font-mono text-muted-foreground">{idx + 1}</span>
                      <Input
                        id={`carico-unit-matricola-${idx}`}
                        value={u.matricola}
                        onChange={(e) => setUnitRows((prev) => prev.map((r, i) => i === idx ? { ...r, matricola: e.target.value } : r))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            document.getElementById(`carico-unit-barcode-${idx}`)?.focus();
                          }
                        }}
                        placeholder="Matricola"
                        className="col-span-5 rounded-none font-mono text-xs h-8"
                        data-testid={`carico-unit-${idx}-matricola`}
                      />
                      <Input
                        id={`carico-unit-barcode-${idx}`}
                        value={u.codice_barre}
                        onChange={(e) => setUnitRows((prev) => prev.map((r, i) => i === idx ? { ...r, codice_barre: e.target.value } : r))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (idx === unitRows.length - 1) {
                              setUnitRows((prev) => [...prev, { matricola: '', codice_barre: '', _uid: uid() }]);
                            }
                            setTimeout(() => document.getElementById(`carico-unit-matricola-${idx + 1}`)?.focus(), 60);
                          }
                        }}
                        placeholder="Codice a barre (EAN)"
                        className="col-span-5 rounded-none font-mono text-xs h-8"
                        data-testid={`carico-unit-${idx}-barcode`}
                      />
                      <button
                        type="button"
                        onClick={() => setUnitRows((prev) => prev.filter((_, i) => i !== idx))}
                        className="col-span-1 text-rose-500 hover:text-rose-700"
                        title="Rimuovi riga"
                        data-testid={`carico-unit-${idx}-remove`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mx-auto" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setUnitRows((prev) => [...prev, { matricola: '', codice_barre: '', _uid: uid() }])}
                  className="rounded-none w-full h-8 text-xs"
                  data-testid="carico-add-unit-row"
                >
                  <Plus className="h-3 w-3 mr-1" /> Aggiungi unità
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  Ogni riga crea un pezzo separato con marca/modello di questo articolo. Puoi sparare il barcode con lo scanner USB nel campo.
                </p>
              </div>
            ) : (
            <div>
              <Label className="text-xs font-mono uppercase">Quantità *</Label>
              <Input
                type="number"
                step="0.01"
                value={movimentoForm.quantita}
                onChange={(e) => setMovimentoForm({...movimentoForm, quantita: e.target.value})}
                className="rounded-none font-mono"
                required
                data-testid="movimento-quantita"
              />
            </div>
            )}
            <div>
              <Label className="text-xs font-mono uppercase">Prezzo Unitario (EUR)</Label>
              <Input
                type="number"
                step="0.01"
                value={movimentoForm.prezzo_unitario}
                onChange={(e) => setMovimentoForm({...movimentoForm, prezzo_unitario: e.target.value})}
                className="rounded-none font-mono"
                data-testid="movimento-prezzo"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Riferimento (DDT, Fattura, Ticket...)</Label>
              <Input
                value={movimentoForm.riferimento}
                onChange={(e) => setMovimentoForm({...movimentoForm, riferimento: e.target.value})}
                className="rounded-none"
                data-testid="movimento-riferimento"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Note</Label>
              <Textarea
                value={movimentoForm.note}
                onChange={(e) => setMovimentoForm({...movimentoForm, note: e.target.value})}
                className="rounded-none"
                data-testid="movimento-note"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setMovimentoDialogOpen(false)} className="rounded-none">
                Annulla
              </Button>
              <Button 
                type="submit" 
                className={`rounded-none font-mono uppercase ${movimentoForm.tipo === 'carico' ? 'bg-success hover:bg-success/90' : 'bg-urgent hover:bg-urgent/90'}`}
                data-testid="save-movimento-btn"
              >
                {movimentoForm.tipo === 'carico' && caricoUnits
                  ? `Carica ${validUnitRows.length} unit\u00e0`
                  : `Registra ${movimentoForm.tipo}`}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Scanner Modal */}
      <BarcodeScannerModal
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={handleScanFromHeader}
        title="Scansiona articolo di magazzino"
      />

      {/* Bulk Carico Dialog */}
      <BulkCaricoDialog
        open={bulkCaricoOpen}
        onOpenChange={setBulkCaricoOpen}
        isSuperAdmin={isSuperAdmin}
        onCreated={() => fetchData()}
      />
    </div>
  );
}
